import { Flex, Icon, IconButton, InputGroup, InputLeftElement, Text, Box, Divider } from '@chakra-ui/react'
import { Filter, LucideX, Tennis } from '../icons/icons'
import { useUserWrapperContext } from '../wrappers/UserWrapper'
import { useEffect, useRef, useState } from 'react'
import LocationSearch from './LocationSearch'
import useMobile from '../../hooks/useMobile'
import { usePlayFilters, SPORT_OPTIONS } from '../../pages/user/playFilters/usePlayFilters'

const PlaySearchFilter = (props: {
  hasMapToggle?: boolean
  collapsed?: boolean
  filterCount?: number
  onClearFilters?: () => void
  onLocationSearch?: (location: { lat: number; lng: number }) => void // Function to update map center
}) => {
  const {
    filterToggled,
    setFilterToggled,
    mapToggled,
    setMapToggled,
    scheduleToggled,
    setScheduleToggled,
    searchPlay,
    setSearchPlay,
    filterActive,
    activeFilterCount,
    clearAllFilters,
  } = useUserWrapperContext()

  const { sport, setSport, sportFilterCount } = usePlayFilters()

  const [customSelectOpen, setCustomSelectOpen] = useState(false)

  const componentRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const isMobile = useMobile(600)

  const baseFilterCount = props.filterCount ?? activeFilterCount ?? 0
  // The filter-button badge reflects ONLY the modal's filters. Sport is an
  // inline filter (outside the modal), so it must not bump this count.
  const modalFilterCount = baseFilterCount
  // The clear pill still accounts for sport so an inline sport selection is clearable.
  const totalFilterCount = baseFilterCount + sportFilterCount
  const onClearFilters = props.onClearFilters ?? clearAllFilters
  const showCountBadge = modalFilterCount > 0
  const showClearPill = totalFilterCount > 0 || filterActive

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        componentRef.current &&
        !componentRef.current?.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current?.contains(event.target)
      ) {
        setCustomSelectOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <>
      <Flex
        gap="10px"
        h={props.collapsed ? '0' : '40px'}
        mb={props.collapsed ? '0' : '10px'}
        mt={isMobile ? '10px' : '0px'}
        transition="0.3s ease-out"
        position="relative"
        overflow="visible"
      >
        <InputGroup
          bgColor="#F5F7F9"
          borderRadius="10px"
          cursor="pointer"
          onClick={() => {
            setCustomSelectOpen(!customSelectOpen)
          }}
          ref={triggerRef}
          _hover={{
            backgroundColor: '#eceef2',
          }}
        >
          <InputLeftElement>
            <Icon w="22px" h="22px">
              <Tennis color="black" />
            </Icon>
          </InputLeftElement>
          <Box p="9px 0px 0px 40px" w="100%" color="black" fontSize="lg">
            {sport ? SPORT_OPTIONS.find((opt) => opt.value === sport)?.label : 'Sport'}
          </Box>
        </InputGroup>
        {customSelectOpen && (
          <Box
            top="50px"
            w="calc(100% - 50px)"
            bgColor="#F5F7F9"
            borderRadius="10px"
            zIndex={1000}
            position="absolute"
            boxShadow="0px 12.914606094360352px 86.71235656738281px 0px #2B2C2D14"
            borderWidth="1px"
            overflow="hidden"
            ref={componentRef}
          >
            {SPORT_OPTIONS.map((option, i) => (
              <Box key={i}>
                <Text
                  px="12px"
                  py="10px"
                  color="#171C22"
                  cursor="pointer"
                  fontWeight="500"
                  transition="background-color 0.15s ease-out"
                  _hover={{ bg: '#eceef2' }}
                  _active={{ bg: '#e2e6ec' }}
                  onClick={() => {
                    setSport(option.value)
                    setCustomSelectOpen(false)
                  }}
                >
                  {option.label}
                </Text>
                <Divider />
              </Box>
            ))}
          </Box>
        )}
        <Flex>
          <IconButton
            onClick={() => {
              setFilterToggled(!filterToggled)
            }}
            aria-label="filters"
            variant="light"
            borderRadius="10px"
            icon={
              <Icon width="25px" height="26px" viewBox="0 0 27 28" mb="4px" ml="3px">
                <Filter color={!filterActive ? 'black' : '#06C270'} fill="black" />
              </Icon>
            }
          />
          {modalFilterCount > 0 && (
            <Box
              borderRadius="100px"
              bg="black"
              w="20px"
              h="20px"
              p="2px"
              position="absolute"
              top="-6px"
              right="-6px"
              color="white"
              fontSize="12px"
              display={showCountBadge ? 'flex' : 'none'}
              alignItems="center"
              justifyContent="center"
            >
              {modalFilterCount}
            </Box>
          )}
        </Flex>
      </Flex>
      <Flex gap="10px" mb={props.collapsed ? '0px' : '10px'} visibility={!customSelectOpen ? 'visible' : 'hidden'}>
        <Flex direction="row" bg="#F5F7F9" border="none" borderRadius="10px" w="100%" alignItems={'center'}>
          <Box w="100%">
            <LocationSearch
              location={searchPlay}
              playPage={true}
              setLocation={setSearchPlay}
              setCoordinates={(coords) => props.onLocationSearch && props.onLocationSearch(coords)}
              placeholder="Search / Around me"
              fontSize="lg"
            />
          </Box>
        </Flex>
      </Flex>
      {props.hasMapToggle && !props.collapsed && (
        <Flex
          bg="#F5F7F9"
          h="36px"
          p="4px"
          borderRadius="8px"
          mb="10px"
          visibility={!customSelectOpen ? 'visible' : 'hidden'}
        >
          <Flex
            flex="1"
            h="100%"
            alignItems="center"
            justifyContent="center"
            gap="6px"
            px="10px"
            py="8px"
            borderRadius="6px"
            cursor="pointer"
            bg={scheduleToggled ? 'white' : 'transparent'}
            boxShadow={scheduleToggled ? '2px 2px 3px rgba(0,0,0,0.06)' : 'none'}
            onClick={() => setScheduleToggled(true)}
          >
            <Box
              as="svg"
              w="16px"
              h="16px"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#171C22"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              flexShrink={0}
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </Box>
            <Text fontSize="13px" lineHeight="18px" color="#171C22" fontWeight="400">
              Schedule
            </Text>
          </Flex>
          <Flex
            flex="1"
            h="100%"
            alignItems="center"
            justifyContent="center"
            gap="6px"
            px="10px"
            py="8px"
            borderRadius="6px"
            cursor="pointer"
            bg={!mapToggled && !scheduleToggled ? 'white' : 'transparent'}
            boxShadow={!mapToggled && !scheduleToggled ? '2px 2px 3px rgba(0,0,0,0.06)' : 'none'}
            onClick={() => {
              setScheduleToggled(false)
              setMapToggled(false)
            }}
          >
            <Box
              as="svg"
              w="16px"
              h="16px"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#171C22"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              flexShrink={0}
            >
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </Box>
            <Text fontSize="13px" lineHeight="18px" color="#171C22" fontWeight="400">
              List View
            </Text>
          </Flex>
          <Flex
            flex="1"
            h="100%"
            alignItems="center"
            justifyContent="center"
            gap="6px"
            px="10px"
            py="8px"
            borderRadius="6px"
            cursor="pointer"
            bg={mapToggled && !scheduleToggled ? 'white' : 'transparent'}
            boxShadow={mapToggled && !scheduleToggled ? '2px 2px 3px rgba(0,0,0,0.06)' : 'none'}
            onClick={() => {
              setScheduleToggled(false)
              setMapToggled(true)
            }}
          >
            <Box
              as="svg"
              w="16px"
              h="16px"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#171C22"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              flexShrink={0}
            >
              <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
              <line x1="9" y1="3" x2="9" y2="18" />
              <line x1="15" y1="6" x2="15" y2="21" />
            </Box>
            <Text fontSize="13px" lineHeight="18px" color="#171C22" fontWeight="400">
              Map View
            </Text>
          </Flex>
        </Flex>
      )}
      {showClearPill && !props.collapsed && (
        <Flex
          alignSelf="flex-start"
          alignItems="center"
          gap="8px"
          border="0.5px solid #DCE2EA"
          borderRadius="70px"
          px="14px"
          py="10px"
          mb="10px"
          cursor="pointer"
          onClick={() => onClearFilters?.()}
          _hover={{ bg: '#F5F7F9' }}
          width="fit-content"
        >
          <Text fontSize="13px" lineHeight="18px" fontWeight="500" color="#171C22" whiteSpace="nowrap">
            {totalFilterCount > 0
              ? `Clear ${totalFilterCount} ${totalFilterCount === 1 ? 'Filter' : 'Filters'}`
              : 'Clear Filters'}
          </Text>
          <Icon w="16px" h="16px" viewBox="0 0 16 16" color="#171C22">
            <LucideX />
          </Icon>
        </Flex>
      )}
    </>
  )
}

export default PlaySearchFilter
